import React, { Component } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import {
  AppBar,
  Box,
  Dialog,
  Toolbar,
  Button,
  Grid,
  Paper,
  InputBase,
  Chip,
  Typography,
  List,
  Divider,
} from '@mui/material';
import moment from 'moment';
import FilterListIcon from '@mui/icons-material/FilterList';
import CancelIcon from '@mui/icons-material/Cancel';
import SearchIcon from '@mui/icons-material/Search';
import IconButton from '@mui/material/IconButton';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import RescuerScheduleDateFilter from './RescuerScheduleDateFilter';
import DistanceSliderFilter, { DISTANCE_ALL_VALUE, getDistanceLabel } from './filters/DistanceSliderFilter';
import DaysSelectorFilter from './filters/DaysSelectorFilter';
import DonorReceiverFilter from './filters/DonorReceiverFilter';
import SiteSelectorFilter from './filters/SiteSelectorFilter';
import { get } from 'lodash';
import RescueAvailabilityFilter from './filters/RescueAvailabilityFilter';
import RescueSizeFilter from './filters/RescueSizeFilter';
import DateSelectorFilter from './filters/DateSelectorFilter';
import CitySelectorFilter from "./filters/CitySelectorFilter";
import DonorLinkSelectorFilter from './filters/DonorLinkSelectorFilter';
import TimeSelectorFilter from './filters/TimeSelectorFilter';

const removeFilterSx = {
  cursor: 'pointer',
  color: 'grey.400',
};
const filterSx = {
  textAlign: 'left',
  padding: '6px 16px',
  fontSize: '0.875rem',
  fontWeight: 500,
  userSelect: 'none',
  backgroundColor: '#fff',
  color: 'rgba(0, 0, 0, 0.45)',
  border: '1px solid rgba(0, 0, 0, 0.12)',
  lineHeight: 1.75,
  fontFamily: 'Figtree, Helvetica, Arial',
  borderRadius: '4px',
  letterSpacing: '0.02857em',
  textTransform: 'uppercase',
};

class RescuerScheduleFilters extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isDialogOpen: false,
    };
  }

  renderDaysLabel = () => {
    const { rescuerScheduleFilters } = this.props;

    if (rescuerScheduleFilters.days.value.length === 7) {
      return 'Days: all';
    }

    return (
      <Box alignItems="left" display="flex">
        <Box>{`Days: `}</Box>
        <Box>
          <Box
            component="ul"
            sx={{
              float: 'left',
              listStyle: 'none',
              paddingLeft: 5,
              padding: 0,
              margin: 0,
              '& li': {
                display: 'inline-block',
              },
            }}
          >
            {rescuerScheduleFilters.days.value.map((day, i, arr) => (
              <React.Fragment key={`${day}_wrapper`}>
                <Box
                  component="li"
                  sx={[moment(rescuerScheduleFilters.date.value).format('ddd') === day && { color: 'primary.main' }]}
                  key={day}
                >
                  {day}
                </Box>
                {arr.length - 1 !== i && <li key={`${day}+delimiter`}>,</li>}
              </React.Fragment>
            ))}
          </Box>
        </Box>
      </Box>
    );
  };

  updateScheduleFilter = (key, value) => {
    const { setScheduleFilter } = this.props;

    return setScheduleFilter(key, { value: value });
  };

  closeModal = () => this.setState({ isDialogOpen: false });

  render() {
    const {
      classes,
      setScheduleFilter,
      rescuerScheduleFilters,
      rows,
      donorReceiverList,
      isMobileView,
      maxDate,
      sitesWithRescuerRoles,
      pickupCities,
      dropoffCities,
      hasActiveDonorLinkFF,
      sites,
    } = this.props;
    const { isDialogOpen } = this.state;
    const currentDay = moment(rescuerScheduleFilters.date.value).toDate();
    const isNextDateDisabled = currentDay >= maxDate;
    const sitesData = sitesWithRescuerRoles.map((siteId) => sites.byId[siteId]);

    return (
      <Box sx={{ width: '100%', marginBottom: 2 }}>
        <Grid>
          <Paper
            sx={(theme) => ({
              marginBottom: '15px',
              borderRadius: theme.shape.borderRadius,
              backgroundColor: theme.palette.primary.main,
              padding: '10px 10px 10px 10px',
              width: '100%',
              [theme.breakpoints.up('sm')]: {
                display: 'none',
              },
            })}
          >
            <Paper sx={{ backgroundColor: '#fff', display: 'flex' }}>
              {rescuerScheduleFilters.query.value.length === 0 && (
                <IconButton sx={{ padding: '10px' }} aria-label="clear search filter" size="large">
                  <SearchIcon />
                </IconButton>
              )}
              {rescuerScheduleFilters.query.value.length > 0 && (
                <IconButton
                  sx={{ padding: '10px' }}
                  aria-label="menu"
                  onClick={() => setScheduleFilter('query', { value: '' })}
                  size="large"
                >
                  <HighlightOffIcon />
                </IconButton>
              )}
              <InputBase
                value={rescuerScheduleFilters.query.value}
                onChange={event => setScheduleFilter('query', { value: event.target.value })}
                sx={{ marginLeft: 1, marginRight: 1, flex: 1 }}
                inputProps={{ 'aria-label': 'filter results' }}
              />
            </Paper>
          </Paper>
        </Grid>
        <Grid container direction="row" justifyContent="flex-start" alignItems="center" spacing={2}>
          <Grid item>
            <Button
              data-introid="schedule-filters"
              variant="contained"
              color="primary"
              startIcon={<FilterListIcon />}
              onClick={() => this.setState({ isDialogOpen: true })}
            >
              Filters
            </Button>
          </Grid>

          <Grid item hidden={rescuerScheduleFilters.distance.value === DISTANCE_ALL_VALUE}>
            <Chip
              icon={
                <CancelIcon
                  sx={{ cursor: 'pointer', color: 'grey.400' }}
                  onClick={() => setScheduleFilter('distance', { value: DISTANCE_ALL_VALUE })}
                />
              }
              sx={filterSx}
              label={getDistanceLabel(rescuerScheduleFilters.distance.value)}
              variant="outlined"
              color="default"
              style={{ height: 36 }}
            />
          </Grid>

          <Grid
            item
            hidden={
              rescuerScheduleFilters?.pickupCities?.value?.length === 0 ||
              rescuerScheduleFilters?.pickupCities?.value?.length === pickupCities.length
            }
          >
            <Chip
              icon={
                <CancelIcon
                  sx={removeFilterSx}
                  onClick={() => setScheduleFilter('pickupCities', { value: [] })}
                />
              }
              sx={filterSx}
              label={
                rescuerScheduleFilters?.pickupCities?.value?.length > 1
                  ? `Pickup Cities: ${rescuerScheduleFilters?.pickupCities?.value?.length}`
                  : `Pickup City: ${rescuerScheduleFilters?.pickupCities?.value[0]}`
              }
              variant="outlined"
              color="default"
              style={{ height: 36 }}
            />
          </Grid>

          <Grid
            item
            hidden={
              rescuerScheduleFilters?.dropoffCities?.value?.length === 0 ||
              rescuerScheduleFilters?.dropoffCities?.value?.length === dropoffCities.length
            }
          >
            <Chip
              icon={
                <CancelIcon
                  sx={removeFilterSx}
                  onClick={() => setScheduleFilter('dropoffCities', { value: [] })}
                />
              }
              sx={filterSx}
              label={
                rescuerScheduleFilters?.dropoffCities?.value?.length > 1
                  ? `Dropoff Cities: ${rescuerScheduleFilters?.dropoffCities?.value?.length}`
                  : `Dropoff City: ${rescuerScheduleFilters?.dropoffCities?.value[0]}`
              }
              variant="outlined"
              color="default"
              style={{ height: 36 }}
            />
          </Grid>

          <Grid
            item
            hidden={
              rescuerScheduleFilters?.donorReceiverList?.value?.length === 0 ||
              rescuerScheduleFilters?.donorReceiverList?.value?.length === donorReceiverList.length
            }
          >
            <Chip
              icon={
                <CancelIcon
                  sx={removeFilterSx}
                  onClick={() => setScheduleFilter('donorReceiverList', { value: [] })}
                />
              }
              sx={filterSx}
              label={
                rescuerScheduleFilters?.donorReceiverList.value.length > 1
                  ? `Donor / Receiving Agency: ${rescuerScheduleFilters?.donorReceiverList?.value?.length}`
                  : `Donor / Receiving Agency: ${rescuerScheduleFilters?.donorReceiverList?.value[0]?.title}`
              }
              variant="outlined"
              color="default"
              style={{ height: 36 }}
            />
          </Grid>

          <Grid item hidden={rescuerScheduleFilters.rescueSizes.value.length === 0}>
            <Chip
              icon={
                <CancelIcon
                  sx={removeFilterSx}
                  onClick={() => setScheduleFilter('rescueSizes', { value: [] })}
                />
              }
              sx={filterSx}
              label={`Vehicle Size(s): ${rescuerScheduleFilters.rescueSizes.value.length}`}
              variant="outlined"
              color="default"
              style={{ height: 36 }}
            />
          </Grid>

          {rescuerScheduleFilters.days.value.length > 0 && (
            <Grid item>
              <Chip
                icon={
                  <CancelIcon
                    sx={removeFilterSx}
                    onClick={() => setScheduleFilter('days', { value: [] })}
                  />
                }
                sx={filterSx}
                label={this.renderDaysLabel()}
                variant="outlined"
                color="default"
                style={{ height: 36 }}
              />
            </Grid>
          )}

          <Grid
            item
            hidden={
              rescuerScheduleFilters?.siteIds?.value?.length === 0 ||
              rescuerScheduleFilters?.siteIds?.value?.length === sitesWithRescuerRoles.length
            }
          >
            <Chip
              icon={
                <CancelIcon
                  sx={removeFilterSx}
                  onClick={() => setScheduleFilter('siteIds', { value: [] })}
                />
              }
              sx={filterSx}
              label={
                rescuerScheduleFilters?.siteIds?.value?.length > 1
                  ? `Sites: ${rescuerScheduleFilters?.siteIds?.value?.length}`
                  : `Site: ${sitesData.find((site) => site?.id === rescuerScheduleFilters?.siteIds?.value[0])?.name}`
              }
              variant="outlined"
              color="default"
              style={{ height: 36 }}
            />
          </Grid>

          <Grid item hidden={!rescuerScheduleFilters.date.value}>
            <Chip
              icon={
                <CancelIcon
                  sx={removeFilterSx}
                  onClick={() => setScheduleFilter('date', { value: null })}
                />
              }
              sx={filterSx}
              label={`Date: ${moment(rescuerScheduleFilters.date.value).format('MM/DD')}`}
              variant="outlined"
              color="default"
              style={{ height: 36 }}
            />
          </Grid>

          {rescuerScheduleFilters?.after?.value && (
            <Grid item>
              <Chip
                icon={
                  <CancelIcon
                    sx={removeFilterSx}
                    onClick={() => setScheduleFilter('after', { value: null })}
                  />
                }
                sx={filterSx}
                label={`Pickup after: ${moment(rescuerScheduleFilters.after.value, 'HH:mm:ss').format('hh:mm a')}`}
                variant="outlined"
                color="default"
                style={{ height: 36 }}
              />
            </Grid>
          )}

          {rescuerScheduleFilters?.before?.value && (
            <Grid item>
              <Chip
                icon={
                  <CancelIcon
                    sx={removeFilterSx}
                    onClick={() => setScheduleFilter('before', { value: null })}
                  />
                }
                sx={filterSx}
                label={`Pickup before: ${moment(rescuerScheduleFilters.before.value, 'HH:mm:ss').format('hh:mm a')}`}
                variant="outlined"
                color="default"
                style={{ height: 36 }}
              />
            </Grid>
          )}

          <Grid item hidden={!rescuerScheduleFilters?.donorLink?.value}>
            <Chip
              icon={
                <CancelIcon
                  sx={removeFilterSx}
                  onClick={() => setScheduleFilter('donorLink', { value: null })}
                />
              }
              sx={filterSx}
              label={`Donor Link: ${rescuerScheduleFilters?.donorLink?.value}`}
              variant="outlined"
              color="default"
              style={{ height: 36 }}
            />
          </Grid>
        </Grid>

        {isMobileView && (<RescuerScheduleDateFilter disableNextDate={isNextDateDisabled} />)}

        <Dialog fullScreen={isMobileView} fullWidth maxWidth="sm" open={isDialogOpen} onClose={this.closeModal}>
          <AppBar sx={{ position: 'relative' }}>
            <Toolbar>
              <Typography variant="h6" sx={{ marginLeft: 2, flex: 1 }}>
                {`Filters (${rows.length} results)`}
              </Typography>
              <Button autoFocus color="inherit" onClick={this.closeModal}>
                save
              </Button>
            </Toolbar>
          </AppBar>

          <List>
            <DistanceSliderFilter
              value={rescuerScheduleFilters.distance.value}
              handleSliderChange={value => this.updateScheduleFilter('distance', value)}
            />
            <Divider />

            <CitySelectorFilter
              label="Pickup City"
              cities={pickupCities}
              value={get(rescuerScheduleFilters, 'pickupCities.value', [])}
              handleChange={value => this.updateScheduleFilter('pickupCities', value)}
            />

            <Divider />

            <CitySelectorFilter
              label="Dropoff City"
              cities={dropoffCities}
              value={get(rescuerScheduleFilters, 'dropoffCities.value', [])}
              handleChange={value => this.updateScheduleFilter('dropoffCities', value)}
            />

            <Divider />

            <DaysSelectorFilter
              disableNextDat={isNextDateDisabled}
              value={rescuerScheduleFilters.days.value}
              handleDaysChange={value => this.updateScheduleFilter('days', value)}
            />
            <Divider />
            <DonorReceiverFilter
              options={donorReceiverList}
              value={rescuerScheduleFilters.donorReceiverList.value}
              handleDonorReceiverChange={value => this.updateScheduleFilter('donorReceiverList', value)}
            />
            <Divider />
            <RescueSizeFilter
              value={rescuerScheduleFilters.rescueSizes.value}
              handleSizesChange={value => this.updateScheduleFilter('rescueSizes', value)}
            />
            <Divider />
            {sitesWithRescuerRoles.length > 1 && (
              <>
                <SiteSelectorFilter
                  sitesWithRescuerRoles={sitesWithRescuerRoles}
                  value={get(rescuerScheduleFilters, 'siteIds.value', [])}
                  handleSiteIdsChange={value => this.updateScheduleFilter('siteIds', value)}
                />
                <Divider />
              </>
            )}
            <DateSelectorFilter
              disableNextDate={isNextDateDisabled}
              value={rescuerScheduleFilters.date.value}
              handleDateChange={(value) => this.updateScheduleFilter('date', value)}
            />
            <Divider />
            <TimeSelectorFilter
              afterValue={get(rescuerScheduleFilters, 'after.value', false)}
              beforeValue={get(rescuerScheduleFilters, 'before.value', false)}
              handleAfterChange={(value) => this.updateScheduleFilter('after', value)}
              handleBeforeChange={(value) => this.updateScheduleFilter('before', value)}
            />
            <Divider />
            {hasActiveDonorLinkFF && (
              <>
                <DonorLinkSelectorFilter
                  value={get(rescuerScheduleFilters, 'donorLink.value', false)}
                  handleFilterChange={value => this.updateScheduleFilter('donorLink', value)}
                />
                <Divider />
              </>
            )}
            <RescueAvailabilityFilter
              value={get(rescuerScheduleFilters, 'showOnlyAvailableRescues.value', false)}
              handleAvailabilityChange={value => this.updateScheduleFilter('showOnlyAvailableRescues', value)}
            />
          </List>
        </Dialog>
      </Box>
    );
  }
}

const mapStateToProps = ({ entities: { sites } }) => ({
  sites,
});

export default compose(connect(mapStateToProps, null))(RescuerScheduleFilters);
